import type { Dictionary } from "lodash";

import type {
    CategoryType,
    ColourType,
    ExtraType,
    PackType,
    UpholsteryType,
    WheelType,
} from "../../../../common-deprecated/types/CommonTypes";
import type { CategoryIconType } from "./types/EquipmentTypes";
import { CategoryIcon } from "./types/EquipmentTypes";

type GroupColoursBasedOnPackType = { [key: string]: { [key: string]: ColourType[] } };

/**
 * Groups colours that are already grouped by type (category) in aditional groups per pack.
 * All colours that are not from a pack are grouped as "standard".
 */
export const groupColoursBasedOnPack = (
    coloursGroupedByType: Dictionary<ColourType[]>,
    packs: PackType[],
): GroupColoursBasedOnPackType => {
    const result: GroupColoursBasedOnPackType = {};

    Object.keys(coloursGroupedByType).forEach((colourKey) => {
        const colours = coloursGroupedByType[colourKey];

        colours.forEach((colour) => {
            const packId =
                packs.find((pack) =>
                    pack.equipment.find(
                        (equipment) =>
                            "exteriorColours" in equipment &&
                            equipment.exteriorColours.find((packColour) => packColour.shortId === colour.shortId),
                    ),
                )?.shortId ?? "standard";

            if (!result[packId]) {
                result[packId] = {};
            }

            result[packId][colourKey] = colours;

            return result;
        });
    });

    return result;
};

export type GroupUpholsteriesBasedOnPackType = { [key: string]: (ExtraType | UpholsteryType)[] };

/**
 * Groups upholsteries per pack.
 * All upholsteries that are not from a pack are grouped as "standard".
 */
export const groupUpholsteriesBaseOnPack = (
    upholsteries: (ExtraType | UpholsteryType)[],
    packs: PackType[],
): GroupUpholsteriesBasedOnPackType => {
    const result: GroupUpholsteriesBasedOnPackType = {};

    upholsteries.forEach((upholstery) => {
        const packId =
            packs.find((pack) =>
                pack.equipment.find(
                    (equipment) =>
                        "upholsteries" in equipment &&
                        equipment.upholsteries.find((packUpholstery) => packUpholstery.shortId === upholstery.shortId),
                ),
            )?.shortId ?? "standard";

        if (!result[packId]) {
            result[packId] = upholsteries;
        }

        return result;
    });

    return result;
};

/**
 * Group Extras by Category
 */
export const groupExtrasByCategory = (items: ExtraType[]): Dictionary<Dictionary<ExtraType[]>> => {
    const categorizedItems = items.reduce((acc: Dictionary<Dictionary<ExtraType[]>>, item: ExtraType) => {
        const rootCategory = `${item.category.root?.code || "root"}|${item.category.root?.name || "root"}|${
            item.category.root?.sortIndex || 0
        }`;
        const currentCategory = `${item.category.code}|${item.category.name}|${item.category.sortIndex}`;
        return {
            ...acc,
            [rootCategory]: {
                ...(acc[rootCategory] || {}),
                [currentCategory]: [
                    ...(acc[rootCategory] && acc[rootCategory][currentCategory]
                        ? [...acc[rootCategory][currentCategory], item]
                        : [item]),
                ],
            },
        };
    }, {});

    // Sort each category
    return categorizedItems;
};

/**
 * Group Packs by Category
 * @note A pack can contain equipment from multiple categories, thus can show up in multiple categories
 */
export const groupPacksByCategory = (
    packs: PackType[],
    enabledCategories: CategoryType[],
): Dictionary<Dictionary<PackType[]>> => {
    const enabledCategoryIds = enabledCategories.map(({ id }) => id);
    const categorizedPacks = packs.reduce((acc: Dictionary<Dictionary<PackType[]>>, pack: PackType) => {
        const packCategories: CategoryType[] = [];
        pack.equipment.forEach((equipmentItem) => {
            if (
                (!enabledCategoryIds.length ||
                    (!!enabledCategoryIds.length &&
                        enabledCategoryIds.includes((equipmentItem as ExtraType)?.category.id))) &&
                !packCategories.find((cat) => cat.id === (equipmentItem as ExtraType)?.category.id)
            ) {
                packCategories.push((equipmentItem as ExtraType).category);
            }
        });
        return packCategories.reduce(
            (packAcc: Dictionary<Dictionary<PackType[]>>, category) => {
                const rootCategory = `${category.root?.code || "root"}|${category.root?.name || "root"}|${
                    category.root?.sortIndex || 0
                }`;
                const currentCategory = `${category.code}|${category.name}|${category.sortIndex}`;
                // Prevent duplicates & reinforce filters
                if (
                    packAcc[rootCategory] &&
                    packAcc[rootCategory][currentCategory] &&
                    packAcc[rootCategory][currentCategory].includes(pack)
                ) {
                    return packAcc;
                }

                return {
                    ...packAcc,
                    [rootCategory]: {
                        ...(packAcc[rootCategory] || {}),
                        [currentCategory]: [
                            ...(packAcc[rootCategory] && packAcc[rootCategory][currentCategory]
                                ? [...packAcc[rootCategory][currentCategory], pack]
                                : [pack]),
                        ],
                    },
                };
            },
            {
                ...acc,
            },
        );
    }, {});

    return categorizedPacks;
};

export const getCategoryKey = (code: string): string => {
    return code.split("|")[0];
};

export const getCategoryNameForCollections = (code: string): string | undefined => {
    return code.split("|")[1];
};

/**
 * Gets the categoryRoot icon
 * @param categoryRoot
 * @returns an icon string or nothing
 */
export const getCategoryGroupIcon = (categoryRoot: string): CategoryIconType | undefined => {
    const catType = categoryRoot.split("|")[0];
    if (catType === "EXT") return CategoryIcon.Exterior;
    if (catType === "INT") return CategoryIcon.Interior;
    return undefined;
};

export const sortCategories = <T>(a: [string, T], b: [string, T]): number => {
    return parseInt(a[0].split("|")[2] || "0") - parseInt(b[0].split("|")[2] || "0");
};

export const findPackExtraImage = (
    basketPacks: PackType[],
    basketExtras: ExtraType[],
    allPacksAndExtras?: { possiblePacks: PackType[]; possibleExtras: ExtraType[] },
): string | null => {
    const findPackExtraHelper = (packs: PackType[], extras: ExtraType[]): string | null => {
        if (extras.length < 0 && packs.length < 0) return null;

        const packWithImage = packs.find((pack) => pack.image !== null && pack.image !== "");
        if (packWithImage?.image) return packWithImage.image;

        let packEquipmentWithImage: WheelType | ExtraType | undefined;
        packs.forEach((pack) => {
            if (!packEquipmentWithImage) {
                pack.equipment.forEach((equipment) => {
                    if (!packEquipmentWithImage) {
                        if (equipment.image !== null && equipment.image !== "") {
                            packEquipmentWithImage = equipment;
                        }
                    }
                });
            }
        });
        if (packEquipmentWithImage?.image) return packEquipmentWithImage.image;

        const extraWithImage = extras.find((extra) => !!extra.image);
        if (extraWithImage?.image) return extraWithImage.image;

        return null;
    };

    const image = findPackExtraHelper(basketPacks, basketExtras);

    if (!image && allPacksAndExtras)
        return findPackExtraHelper(allPacksAndExtras.possiblePacks, allPacksAndExtras.possibleExtras);

    return image;
};
